import { Flex } from 'components/shared/flex'
import { usePostTaskMasterSetApi, usePutTaskMasterSetApi } from 'api/task_master_set'
import { InputField, SelectField } from 'components/shared/input'
import { GlobalStateContext } from 'contexts/global_state_context'
import { useContext, useEffect, useState } from 'react'
import { Button, CloseButton, Modal } from 'react-bootstrap'
import { Form, useEffectSkipFirst, useForm } from 'utils/hooks'
import { ApiError } from 'utils/network/api_hooks'
import { TaskMasterSet, TaskMasterSetForm, TaskMasterSetKind } from 'entities/task_master_set'
import { TaskMaster } from 'entities/task_master'
import { Material } from 'entities/material'
import { useFetchMaterialsApi } from 'api/material'
import { FaPlus } from 'react-icons/fa'
import { MaterialTaskData } from 'entities/task'

export type FormViewProps = {
    form: Form<TaskMasterSetForm>
    apiError: ApiError
}

const FormView = ({ form, apiError }: FormViewProps) => {
    const materialsApi = useFetchMaterialsApi()

    useEffect(() => {
        if (form.object.kind === 'MATERIAL') {
            materialsApi.execute()
        }
    }, [form.object])

    return (
        <>
            <Flex>
                <InputField form={form} attr="title" apiError={apiError} label="タイトル" required />
            </Flex>
            {!form.object.kind && <InputField form={form} attr="kind" apiError={apiError} label="種別" required />}

            <div style={{ marginBottom: 10 }}>タスク</div>
            {form.object.taskMasters?.map((taskMaster, index) => {
                return (
                    <div key={'task-master-' + index}>
                        {form.object.kind === 'MATERIAL' ? (
                            <MaterialTaskFormView taskMaster={taskMaster} index={index} materials={materialsApi.response.results} form={form} />
                        ) : form.object.kind !== undefined ? (
                            <div>他にタスク表現があればここにViewを追加する（ここの処理はメモ程度のものなので実際は不要）</div>
                        ) : (
                            <InputField form={form} attr={['taskMasters', index, 'title']} />
                        )}
                    </div>
                )
            })}
            <Button
                onClick={() => {
                    form.update((f) => {
                        f.taskMasters?.push({})
                    })
                }}
                variant="secondary"
                style={{ width: '100%' }}
            >
                <FaPlus size={12} style={{ marginRight: 10 }} />
                タスク追加
            </Button>
        </>
    )
}

type MaterialTaskFormViewProps = {
    form: Form<TaskMasterSetForm>
    index: number
    taskMaster: TaskMaster
    materials: Material[]
}

type MaterialTaskForm = {
    materialInfo?: string
    number?: number
}

const MaterialTaskFormView = (props: MaterialTaskFormViewProps) => {
    const tempForm = useForm<MaterialTaskForm>({})

    useEffect(() => {
        const data: MaterialTaskData = JSON.parse(props.taskMaster.data || '{}')
        tempForm.set({
            materialInfo: data.material_id ? `${data.material_id},${data.material_name}` : undefined,
            number: data.number,
        })
    }, [props.taskMaster])

    useEffectSkipFirst(() => {
        if (!tempForm.object.materialInfo || !tempForm.object.number) {
            return
        }
        const [materialId, materialName] = tempForm.object.materialInfo.split(',')

        props.form.update((f) => {
            f.taskMasters![props.index].title = `${materialName} ${tempForm.object.number}部発送`
            f.taskMasters![props.index].data = `{"material_id": ${materialId}, "material_name": "${materialName}", "number": ${tempForm.object.number} }`
        })
    }, [tempForm.object])

    return (
        <Flex style={{ width: '100%' }} alignItems="flex-end">
            <div style={{ flexGrow: 1 }}>
                <SelectField
                    form={tempForm}
                    attr="materialInfo"
                    selectItems={props.materials.map((m) => {
                        return { label: m.name, value: `${m.id},${m.name}` }
                    })}
                    blankLabel="資材を選択してください"
                    includeBlank
                />
            </div>
            <div style={{ width: 100 }}>
                <InputField form={tempForm} attr="number" placeholder="部数" type="number" min={1} />
            </div>
            <CloseButton
                style={{ marginBottom: 22 }}
                onClick={() => {
                    props.form.update((f) => {
                        f.taskMasters!.splice(props.index, 1)
                    })
                }}
            />
        </Flex>
    )
}

type NewTaskMasterSetFormProps = {
    onComplete?: () => void
    kind?: TaskMasterSetKind
}

export const NewTaskMasterSetForm = (props: NewTaskMasterSetFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePostTaskMasterSetApi()
    const form = useForm<TaskMasterSetForm>({ kind: props.kind, taskMasters: [] })
    const [show, setShow] = useState<boolean>(false)

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                setShow(false)
                props.onComplete()
            }
        }
    }, [api.loading])

    const handleClose = () => {
        setShow(false)
    }

    return (
        <>
            <Button
                variant="success"
                onClick={() => {
                    setShow(true)
                }}
            >
                新規登録
            </Button>
            <Modal show={show} onHide={handleClose} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>タスクテンプレートの登録</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <>
                        <FormView form={form} apiError={api.apiError} />
                    </>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        閉じる
                    </Button>
                    <Button variant="success" onClick={handleUpdate}>
                        登録
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}
type EditTaskMasterSetFormProps = {
    taskMasterSet: TaskMasterSet
    show: boolean
    onClose: () => void
    onComplete?: () => void
}

export const EditTaskMasterSetForm = (props: EditTaskMasterSetFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePutTaskMasterSetApi()
    const form = useForm<TaskMasterSetForm>(props.taskMasterSet)

    useEffectSkipFirst(() => {
        form.set(props.taskMasterSet)
    }, [props.taskMasterSet])

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                props.onComplete()
            }
        }
    }, [api.loading])

    return (
        <Modal show={props.show} onHide={props.onClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>タスクテンプレートの編集</Modal.Title>
            </Modal.Header>
            <Modal.Body>{form.object.id && <FormView form={form} apiError={api.apiError} />}</Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={props.onClose}>
                    閉じる
                </Button>
                <Button variant="success" onClick={handleUpdate}>
                    更新
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
